//React and Third-party Libraries
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";

// Material UI Components
import MuiTextField from "@mui/material/TextField";
import { AttachMoney } from "@mui/icons-material";

// Living Design Components
import {
  FormGroup, Grid,
  GridColumn, Heading, Radio, Skeleton, Switch, TextField, useSnackbar, Select
} from "@livingdesign/react";

// Services and API
import { getService, postService } from "../../../axios/axios";
import { BUDGET_SERVICE, COMPOSITE_SEARCH_SERVICE } from "../../../constants/baseURLs";

// Local Components
import AddBudgetByUTBMS from "./AddBudgetByUTBMS";
import AddBudgetByPhase from "./AddBudgetByPhase";
import BreakdownByTenure from "./BreakdownByTenure";

// Constants
import {
  BUDGET_NAME, BUDGET_ORGANIZATION,
  detailModes as modes,
  MATTER, BUDGET_LABELS, CS_SCOPES,
  MATTER_INFORMATION, MATTER_NAME, MATTER_TYPE, MATTER_SUBTYPE, MATTER_DESCRIPTION,
  LEAD_ATTORNEY, APPLICABLE_AFA, RELATIONSHIP_PARTNER, ESTIMATED_HOURS,
  WORK_PERIOD, SCOPE_OF_WORK, OVERVIEW,
  ESTIMATED_BLENDED_RATE, ESTIMATED_TOTAL_HOURS,
  LEAD_PARALEGAL, RISKONNECT, LOM, CFY, NOTES,
  TOTAL_COST,
} from "../../../constants/constants";
import { BUDGET_SAVE_MESSAGE, BUDGET_UPDATE_MESSAGE } from "../../../constants/messages";
import { DISCARD, GO_BACK, PROCEED, SUBMIT } from "../../../constants/actionButtons";

// Helpers and Utils
import { budgetValidationRules } from "../../../Validation/validationRules";
import { amountFormat, filterPayload, parseAmountString } from "../../../helpers/commonUtils";
import { getErrorMessage, getHighestVersion, getMatterData, renderTernary } from '../../../helpers/utils';
import { getNextTenFiscalYears } from "../../../helpers/dateTimeUtils";
import { formCompositeSearchQuery, formCsFilterObject } from "../../../helpers/csQueryUtils";
import { getPeopleIdFromName } from "../../../helpers/apiUtils";
import { splitFullName } from "../../../helpers/tableUtils";

// Redux Actions
import { setVersionDetails } from "../../../store/actions/budgetActions";

// Styles
import { CreateBudgetIntakeStyles } from "./CreateBudgetIntake.styles";
import { JURISDICTION } from "../../../constants/constants";
import SubmitBudgetModal from "../../../components/Modal/SubmitBudgetModal/SubmitBudgetModal";
import Detail from "../../Detail/Detail";
import MuiValidationMessage from "../../../Validation/MuiValidationMessage/MuiValidationMessage";
import Label from "../../../components/TextField/Label/Label";
import OrganizationAutocomplete from "../../../components/Autocomplete/OrganizationAutocomplete/OrganizationAutocomplete";
import MatterAutocomplete from "../../../components/Autocomplete/MatterAutocomplete/MatterAutocomplete";

// Constants
const Title = {
  edit: "Edit Budget",
  create: "Add Budget",
};

const CreateBudgetIntake = () => {
  // Hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { addSnack } = useSnackbar();
  const { register, formState: { errors }, clearErrors } = useForm({ mode: "all" });
  const { state: { budgetId = "0", fromMatter, currentMatterTab, matterName, matterNumber, practiceAreaCode, matterIdentifier, matterType, matterSubType, matterDescription, leadAttorneyName, leadParalegalName, matterState } = {} } = useLocation();
  const classes = CreateBudgetIntakeStyles();

  // Redux State
  const masterDetails = useSelector(state => state.masterData?.masterDatas);
  const versionDetails = useSelector(state => state.budget?.versionDetails);
  const budgetAccessibility = useSelector((state) => state?.accessibility?.applicationAccess?.financial?.budget);

  // Component State
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [mode, setMode] = useState(null);
  const [switchOn, setSwitchOn] = useState(false);
  const [budgetTypeLocal, setBudgetTypeLocal] = useState();
  const [isMatterSelected, setIsMatterSelected] = useState(budgetId!="0");  
  const [matterData, setMatterData] = useState(null);
  const [matterLoading, setMatterLoading] = useState(false);
  const [phases, setPhases] = useState([]);
  const [tenureTableData, setTenureTableData] = useState([]);
  const [enteredCost, setEnteredCost] = useState(0);
  const [leadAttorney, setLeadAttorney] = useState(null);
  const [leadParalegal, setLeadParalegal] = useState(null);
  const [utbmsData, setUtbmsData] = useState([]);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [existingBudgetData, setExistingBudgetData] = useState();
  const [isValid, setIsValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { tenures } = useSelector((state) => state?.masterData?.masterDatas);
  const [organizationData, setOrganizationData] = useState(null);
  const [blendedRate, setBlendedRate] = useState(0);
  const { identifier, companyName, relationshipPartnerName } = useSelector((state) => state?.organization?.organizationDetailValues);
  

  // Initial Data Configuration
  const initialData = {
    budgetType: "Total",
    timePeriod: BUDGET_LABELS.LIFE_OF_MATTER,
    status: "draft",
    startDate: "",
    endDate: "",
    matter: fromMatter ? {
      matterName: matterName, referenceKey: matterNumber, matterId: matterIdentifier,
      practiceAreaCode: practiceAreaCode
    } : null,
    isInternal: true,
    matterId: fromMatter ? matterNumber : null,
    organization: {},
    budgetName: "",
    budgetAmount: "",
    isAutoApproved: false,
    fiscalYear: ""
  };

  const title = { ...Title };
  const [data, setData] = useState({ ...initialData });
  const [detailData, setDetailData] = useState({});
  const [estimatedTotalCost, setEstimatedTotalCost] = useState(0);
  const [estimatedTotalHours, setEstimatedTotalHours] = useState(0);

  // Computed Values
  const tenYears = getNextTenFiscalYears();

  // Effects
  useEffect(() => {
    if (budgetId === "0") {
      setMode(modes.CREATE);
    } else {
      setMode(modes.EDIT);
      getBudgetDetail();
    }
  }, [modes, budgetId]);

  useEffect(() => {
    setOrganizationData({
      identifier,
      companyName,
      relationshipPartnerName
    })
  }, [identifier]);

  useEffect(() => {
    if (fromMatter) {
      const fetchLeadPersonIds = async () => {

        // Create array of promises for parallel execution
        const promises = [];

        // Fetch lead attorney ID if name exists
        let { firstName, lastName } = splitFullName(leadAttorneyName);
        if (firstName) {
          const attorneyPromise = getPeopleIdFromName(firstName, lastName)
            .then((response) => {
              setLeadAttorney(response?.data?.people?.data?.[0]?.id);
            })
            .catch((error) => {
              console.log(error);
            });
          promises.push(attorneyPromise);
        }

        // Fetch lead paralegal ID if name exists
        let name = splitFullName(leadParalegalName);
        if (name.firstName) {
          const paralegalPromise = getPeopleIdFromName(name.firstName, name.lastName)
            .then((response) => {
              setLeadParalegal(response?.data?.people?.data?.[0]?.id);
            })
            .catch((error) => {
              console.log(error);
            });
          promises.push(paralegalPromise);
        }

        // Wait for all promises to settle before setting matter data
        await Promise.allSettled(promises);

        setMatterData({
          matterName: matterName,
          matterType: matterType,
          matterSubType: matterSubType,
          description: matterDescription,
          leadAttorney: leadAttorneyName,
          leadParalegal: leadParalegalName,
          leadAttorneyId: leadAttorney,
          leadParalegalId: leadParalegal,
          matterState: matterState,
          referenceKey: matterNumber,
          identifier: matterIdentifier,
          practiceAreaCode: practiceAreaCode
        });
      };

      fetchLeadPersonIds();
    }
  }, [fromMatter]);

  useEffect(() => {
    const bugetBreakdown = tenures?.masterData
      ?.sort((a, b) => a.value.localeCompare(b.value))
      ?.map((tenure) => {
        return {
          tenure: tenure.value,
          classificationsMapped: tenure.description,
          dedicatedStaff: 0,
          numberOfHours: 0,
          budgetAmount: 0,
          effectiveRate: 0
        }
      })
    setTenureTableData(bugetBreakdown)
  }, [tenures])

  // Validation Functions
  const isBudgetUTBMSValid = (list, savedList) => {
    return true
  };

  useEffect(() => {
    let isValidData = false;
    
    if (!isMatterSelected) {
      isValidData = (data?.matter?.matterId || matterData?.id) && !matterLoading;
    } else {
      const parsedCost = parseAmountString(estimatedTotalCost);
      isValidData = parsedCost > 0 && !matterLoading;
      
      // Additional validation for Task type - check if all UTBMS entries have code and budgetAmount
      if (data?.budgetType?.toLowerCase() === "task" && utbmsData.length > 0) {
        const hasIncompleteUtbmsData = utbmsData.some(entry => 
          !entry.code || !entry.budgetAmount || parseFloat(entry.budgetAmount) <= 0
        );
        if (hasIncompleteUtbmsData) {
          isValidData = false;
        }
      }
    }
    
    setIsValid(isValidData);
    setIsDirty(true);
  }, [data, estimatedTotalCost, estimatedTotalHours, isMatterSelected, matterData, utbmsData, phases, tenureTableData, matterLoading]);

  useEffect(() => {
    setBudgetTypeLocal(data?.budgetType)
    // Reset values when budget type changes
    if (data?.budgetType) {
      setEstimatedTotalCost(0);
      setBlendedRate(0);
      setEstimatedTotalHours(0);
    }
  }, [data?.budgetType]);

  useEffect(() => {
    let timePeriodLabel;
    if (data?.timePeriod == BUDGET_LABELS.LIFE_OF_MATTER) {
      timePeriodLabel = "LOM"
    } else {
      timePeriodLabel = data?.fiscalYear
    }
    handleInputChange("budgetName", budgetTypeLocal + "-" + timePeriodLabel + "-" + companyName)
  }, [budgetTypeLocal, data?.timePeriod, data?.organization, data?.fiscalYear, versionDetails]);

  // Event Handlers
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const onSubmitHandle = () => {
    setLoading(true)
    let additionalFilterQuery = [];
    if (!isMatterSelected) {
      setIsMatterSelected(true);
      setLoading(false);
      return
    }

    if (data?.timePeriod === BUDGET_LABELS.CUSTOM_DATE_RANGE_LABEL) {
      const startYear = Number(data?.fiscalYear?.substring(2)) - 1
      const endYear = Number(data?.fiscalYear?.substring(2))
      additionalFilterQuery = [
        formCsFilterObject("startDate", "gte", `${startYear}-01-01 18:29:59`),// 2024
        formCsFilterObject("endDate", "lte", `${endYear}-12-31 18:29:59`)//2025
      ]
    }

    const filterQuery = {
      "filters": [
        formCsFilterObject("status", "in", [BUDGET_LABELS.STATUS_PENDING, BUDGET_LABELS.STATUS_APPROVED]),
        formCsFilterObject("organizationId", "match", identifier),
        formCsFilterObject("matterId", "match", renderTernary(fromMatter, matterIdentifier, matterData?.id)),
        formCsFilterObject("timePeriod", "match", data?.timePeriod),
        ...additionalFilterQuery
      ],
      "operation": "AND",
      "properties": null
    }
    const query = formCompositeSearchQuery([CS_SCOPES.BUDGET], filterQuery);

    postService(
      COMPOSITE_SEARCH_SERVICE,
      `/composite-search/v1?returnCsv=false`,
      query
    )
      .then((response) => {
        if (response?.data?.budget?.data?.length > 0) {
          setLoading(false)
          dispatch(setVersionDetails(`V${getHighestVersion(response?.data?.budget?.data) + 1}`))
          setExistingBudgetData(response?.data?.budget?.data)
          setSubmitModalOpen(true)
        } else {
          onCreateHandler()
        }
      })
      .catch((error) => {
        //Silent Failure
        onCreateHandler()
      });
  };

  const onCloseModal = () => {
    dispatch(setVersionDetails("V1"))
  };

  const handleRemoveTenureRow = (index) => {
    const updatedData = tenureTableData.filter((_, i) => i !== index);
    setTenureTableData(updatedData);
  };

  const handleTenureDataChange = (rowIndex, fieldName, value) => {
    const updatedData = tenureTableData.map((row, index) => {
      if (index === rowIndex) {
        const updatedRow = {
          ...row,
          [fieldName]: value
        };

        // Auto-calculate effective rate when hours or cost changes
        const numberOfHours = parseFloat(fieldName === 'numberOfHours' ? value : updatedRow.numberOfHours) || 0;
        const budgetAmount = parseFloat(fieldName === 'budgetAmount' ? value : updatedRow.budgetAmount) || 0;

        if (numberOfHours > 0 && budgetAmount > 0) {
          updatedRow.effectiveRate = (budgetAmount / numberOfHours).toFixed(2);
        } else {
          updatedRow.effectiveRate = "";
        }

        return updatedRow;
      }
      return row;
    });
    if (budgetTypeLocal === "Total") {
      // Calculate breakdown totals
      const breakdownTotalCost = updatedData.reduce((sum, entry) => {
        const budgetAmount = parseAmountString(entry.budgetAmount) || 0;
        return sum + budgetAmount;
      }, 0);
      const breakdownTotalHours = updatedData.reduce((sum, entry) => {
        const numberOfHours = parseAmountString(entry.numberOfHours) || 0;
        return sum + numberOfHours;
      }, 0);
      
      // Set estimated total cost as max of breakdown total and user entered cost
      const maxCost = Math.max(breakdownTotalCost, enteredCost || 0);
      setEstimatedTotalCost(amountFormat(maxCost));
      setEstimatedTotalHours(amountFormat(breakdownTotalHours));
    }
    setTenureTableData(updatedData);
  };

  const handleInputChange = async (name, value) => {
    if (name === "budgetAmount" && value !== "" && value < 1) {
      return
    }
    if (name === "budgetAmount" && value !== "" && value > 1) {
      const budgetAmountValue = Math.trunc((parseFloat(value) + Number.EPSILON) * 100) / 100
      setData({ ...data, [name]: budgetAmountValue });
      return
    }
    if (name === "fiscalYear" && value !== "") {
      const startDate = `${Number(value?.slice(2)) - 1}-02-01`;
      const endDate = `${Number(value?.slice(2))}-01-31`;
      setData({ ...data, [name]: value, ["startDate"]: startDate, ["endDate"]: endDate });
      return
    }
    if (name === "matter") {
      if (!value?.matterId) {
        setMatterData(null);
        return
      }
      setMatterLoading(true);
        
      await getMatterData(value?.matterId, identifier)
        .then((response) => {
          setMatterData(response?.data?.result?.matter);

          let { firstName, lastName } = splitFullName(response?.data?.result?.matter?.leadAttorney);
          if (firstName) {
            getPeopleIdFromName(firstName, lastName)
              .then((response) => {
                setLeadAttorney(response?.data?.people?.data?.[0]?.id);
              })
              .catch((error) => {
                console.log(error);
              })
          }
          let name = splitFullName(response?.data?.result?.matter?.leadParalegal);
          if (name.firstName) {
            getPeopleIdFromName(name.firstName, name.lastName)
              .then((response) => {
                setLeadParalegal(response?.data?.people?.data?.[0]?.id);
              })
              .catch((error) => {
                console.log(error);
              })
          }
        })
        .catch((error) => {
          addSnack({
            message: getErrorMessage(error)
          });
        })
        .finally(() => {
          setMatterLoading(false);
        });
    }
    if (name === "estimatedTotalCost") {
      const parsedValue = parseAmountString(value);
      setEstimatedTotalCost(parsedValue);
    }
    setData({ ...data, [name]: value });
  };

  const handleUtbmsDataChange = (utbmsData) => {
    if (budgetTypeLocal === "Task") {
      const totalCost = utbmsData.reduce((sum, entry) => {
        const budgetAmount = parseFloat(entry.budgetAmount) || 0;
        return sum + budgetAmount;
      }, 0);
      const totalHours = utbmsData.reduce((sum, entry) => {
        const numberOfHours = parseFloat(entry.numberOfHours) || 0;
        return sum + numberOfHours;
      }, 0);
      setEstimatedTotalCost(amountFormat(totalCost));
      setEstimatedTotalHours(amountFormat(totalHours));
      setUtbmsData(utbmsData);
      setData({ ...data, utbmsData });
    };
  };

  const handlePhasesChange = (phaseEntries) => {
    if (budgetTypeLocal === "Phase") {
      const totalCost = phaseEntries.reduce((sum, entry) => {
        const budgetAmount = parseFloat(entry.budgetAmount) || 0;
        return sum + budgetAmount;
      }, 0);
      const totalHours = phaseEntries.reduce((sum, entry) => {
        const numberOfHours = parseFloat(entry.numberOfHours) || 0;
        return sum + numberOfHours;
      }, 0);
      setEstimatedTotalCost(amountFormat(totalCost));
      setEstimatedTotalHours(amountFormat(totalHours));
      setPhases(phaseEntries);
    }
  }

  useEffect(() => { 
    const fetchMatterData = async () => {
      if (matterData?.matterName && matterData?.id) {
        setMatterLoading(true);
        try {
          const response = await getMatterData(matterData.id, identifier);
          setMatterData(response?.data?.result?.matter);

          let { firstName, lastName } = splitFullName(response?.data?.result?.matter?.leadAttorney);
          if (firstName) {
            getPeopleIdFromName(firstName, lastName)
              .then((response) => {
                setLeadAttorney(response?.data?.people?.data?.[0]?.id);
              })
              .catch((error) => {
                console.log(error);
              })
          }
          let { paralegalFirstName, paralegalLastName } = splitFullName(response?.data?.result?.matter?.leadParalegal);
          if (paralegalFirstName) {
            getPeopleIdFromName(paralegalFirstName, paralegalLastName)
              .then((response) => {
                setLeadParalegal(response?.data?.people?.data?.[0]?.id);
              })
              .catch((error) => {
                console.log(error);
              })
          }
        } catch (error) {
          addSnack({
            message: getErrorMessage(error)
          });
        } finally {
          setMatterLoading(false);
        }
      }
    };

    fetchMatterData();
  }, [matterData?.matterName]);

  const onCancelHandler = () => {
    dispatch(setVersionDetails("V1"))
    if (fromMatter) {
      if (budgetId === "0") {
        history.push(`/matter/${matterIdentifier}`,
          { fromMatter: fromMatter, currentMatterTab: currentMatterTab })
      } else {
        history.push(`/budgetIntake/${budgetId}/view`,
          { fromMatter: fromMatter, currentMatterTab: currentMatterTab, isRowClick: true })
      }
    } else {
      history.goBack();
    }
  };

  // API Functions
  const getBudgetDetail = async () => {
    setDataLoading(true);
    getService("BUDGET_SERVICE", `/budgets/v1/${budgetId}`)
      .then((res) => {
        const detail = res.data;
        // Set up matter data
        const matter = { 
          matterName: detail.matterName, 
          referenceKey: detail.matterNumber, 
          matterId: detail.matterId, 
          practiceAreaCode: detail?.practiceAreaCode 
        };

        // Set up organization data
        const organization = { 
          organizationName: detail.organizationName, 
          referenceKey: detail.organizationId 
        };

        // Set organization data for matter autocomplete
        setOrganizationData({
          identifier: detail.organizationId,
          companyName: detail.organizationName
        });

        // Set matter data
        setMatterData({
          id: detail.matterId,
          matterNumber: detail.matterNumber,
          matterName: detail.matterName,
          practiceAreaCode: detail.practiceAreaCode
        });

        // Calculate fiscal year from time period
        let fiscalYear = "";
        if (detail.timePeriod === BUDGET_LABELS.CUSTOM_DATE_RANGE_LABEL && detail.endDate) {
          fiscalYear = `FY${moment(detail.endDate).year()}`;
        }

        // Set main form data
        setData({ 
          ...data, 
          ...detail, 
          matter, 
          organization, 
          budgetType: detail.type,
          fiscalYear: fiscalYear,
          budgetAmount: detail.budgetAmount
        });

        // Set estimated totals
        setEstimatedTotalCost(detail.budgetAmount || 0);
        setEstimatedTotalHours(detail.totalNumberOfHours || 0);
        setBlendedRate(detail.blendedRate || 0);

        // Set budget type for local state
        setBudgetTypeLocal(detail.type);

        // Populate tenure table data if exists
        if (detail.budgetTenures && detail.budgetTenures.length > 0) {
          const tenureData = detail.budgetTenures.map(tenure => ({
            tenure: tenure.classification,
            classificationsMapped: tenure.classification, // You may want to map this properly
            dedicatedStaff: tenure.dedicatedStaffCount || 0,
            numberOfHours: tenure.numberOfHours || 0,
            budgetAmount: tenure.budgetAmount || 0,
            effectiveRate: tenure.effectiveRate || 0
          }));
          setTenureTableData(tenureData);
        }

        // Populate phases data if exists
        if (detail.budgetPhases && detail.budgetPhases.length > 0) {
          setPhases(detail.budgetPhases);
        }

        // Populate UTBMS data if exists
        if (detail.budgetTasks && detail.budgetTasks.length > 0) {
          setUtbmsData(detail.budgetTasks);
        }

        // Set other form states
        setSwitchOn(detail?.isAutoApproved || false);
        setDetailData({ ...data, ...detail, matter, organization, budgetType: detail.type });
        setDataLoading(false);
      })
      .catch((error) => {
        setDataLoading(false);
        addSnack({
          message: getErrorMessage(error)
        });
      });
  };


  const onCreateHandler = () => {
    setLoading(true);
    let sparebudgetPhases;
    if (data?.hasOwnProperty("budgetPhases")) {
      sparebudgetPhases = data?.budgetPhases;
      const budgetPhases = data?.budgetPhases?.map((item) => {
        const codes = JSON.parse(item?.code);
        return { ...item, code: codes?.value };
      })
      data.budgetPhases = budgetPhases;
    }

    const body = {
      budgetName: data.budgetName,
      type: data.budgetType,
      budgetAmount: parseAmountString(estimatedTotalCost),
      matterId: fromMatter ? matterIdentifier : matterData?.id,
      matterNumber: fromMatter ? matterNumber : matterData?.matterNumber,
      matterName: fromMatter ? matterName : matterData?.matterName,
      practiceAreaCode: fromMatter ? practiceAreaCode : matterData?.practiceAreaCode,
      organizationId: identifier,
      organizationName: companyName,
      timePeriod: data?.timePeriod,
      blendedRate: parseAmountString(estimatedTotalHours) > 0 ? (parseAmountString(estimatedTotalCost) / parseAmountString(estimatedTotalHours)) : 0,
      totalNumberOfHours: parseAmountString(estimatedTotalHours),
      ...(data?.timePeriod === BUDGET_LABELS.CUSTOM_DATE_RANGE_LABEL && {
        startDate: data?.startDate ? `${data?.startDate} 00:00:00` : "",
        endDate: data?.endDate ? `${data?.endDate} 23:59:59` : "",
        fiscalYear: data?.fiscalYear
      }),
      isInternal: data.isInternal,
      isAutoApproved: false,
      budgetPhases: data.budgetType?.toLowerCase() === "phase" ? phases.map(phase => ({
        code: phase.code,
        displayName: phase.displayName,
        workDescription: phase.workDescription || "",
        budgetAmount: parseFloat(phase.budgetAmount) || 0,
        numberOfHours: parseFloat(phase.numberOfHours) || 0
      })) : [],
      budgetTasks: data.budgetType?.toLowerCase() === "task" ? utbmsData.map(task => ({
        code: task.value,
        displayName: task.displayName,
        budgetAmount: parseFloat(task.budgetAmount) || 0,
        numberOfHours: parseFloat(task.numberOfHours) || 0
      })) : [],
      budgetTenures: tenureTableData?.map(tenure => ({
        classification: tenure.tenure,
        effectiveRate: tenure.effectiveRate || "0",
        numberOfHours: parseFloat(tenure.numberOfHours) || 0,
        budgetAmount: parseFloat(tenure.budgetAmount) || 0,
        dedicatedStaffCount: parseFloat(tenure.dedicatedStaff) || 0
      }))
    };
    const filteredBody = filterPayload(body)
    postService(BUDGET_SERVICE, `/budgets/v1`, filteredBody)
      .then((res) => {
        history.push(`/budget/${res?.data?.id}`,
          { budgetType: data?.budgetType, fromMatter: fromMatter, currentMatterTab: currentMatterTab, isRowClick: true });
        setLoading(false);
        addSnack({
          message: BUDGET_SAVE_MESSAGE,
        });
      })
      .catch((err) => {
        if (data?.hasOwnProperty("budgetPhases")) {
          data.budgetPhases = sparebudgetPhases;
        }
        catchError(err)
      });
  };

  const catchError = (error) => {
    setLoading(false);
    addSnack({
      message: getErrorMessage(error)
    });
  }

  const onUpdateHandler = () => {
    setLoading(true);
    let sparebudgetPhases;
    let budgetPhases = []
    if (data?.hasOwnProperty("budgetPhases") && data?.budgetType?.toLowerCase() === "phase") {
      sparebudgetPhases = data?.budgetPhases;
      budgetPhases = data?.budgetPhases?.map((item) => {
        if (item.hasOwnProperty("new") && item?.new) {
          const codes = JSON.parse(item?.code);
          return { ...item, code: codes?.value };
        }
        return item;
      })
      data.budgetPhases = budgetPhases;
    }
    const body = {
      budgetName: data.budgetName,
      type: data.budgetType,
      budgetAmount: parseAmountString(estimatedTotalCost),
      matterId: matterIdentifier || matterData?.id,
      matterNumber: matterNumber || matterData?.matterNumber,
      matterName: matterName || matterData?.matterName,
      practiceAreaCode: practiceAreaCode || matterData?.practiceAreaCode,
      organizationId: data.organization?.referenceKey,
      organizationName: data.organization?.organizationName,
      timePeriod: data?.timePeriod,
      blendedRate: parseAmountString(estimatedTotalHours) > 0 ? parseAmountString(estimatedTotalCost) / parseAmountString(estimatedTotalHours) : 0,
      totalNumberOfHours: parseAmountString(estimatedTotalHours),
      ...(data?.timePeriod === BUDGET_LABELS.CUSTOM_DATE_RANGE_LABEL && {
        startDate: data?.startDate ? `${data?.startDate} 00:00:00` : "",
        endDate: data?.endDate ? `${data?.endDate} 23:59:59` : "",
        fiscalYear: data?.fiscalYear
      }),
      isInternal: data.isInternal,
      isAutoApproved: false,
      budgetPhases: data.budgetType?.toLowerCase() === "phase" ? phases.map(phase => ({
        code: phase.code,
        displayName: phase.displayName,
        workDescription: phase.workDescription || "",
        budgetAmount: parseAmountString(phase.budgetAmount) || 0,
        numberOfHours: parseAmountString(phase.numberOfHours) || 0
      })) : [],
      budgetTasks: data.budgetType?.toLowerCase() === "task" ? utbmsData.map(task => ({
        code: task.value,
        displayName: task.displayName,
        budgetAmount: parseAmountString(task.budgetAmount) || 0,
        numberOfHours: parseAmountString(task.numberOfHours) || 0
      })) : [],
      budgetTenures: tenureTableData?.map(tenure => ({
        classification: tenure.tenure,
        effectiveRate: tenure.effectiveRate || "0",
        numberOfHours: parseAmountString(tenure.numberOfHours) || 0,
        budgetAmount: parseAmountString(tenure.budgetAmount) || 0,
        dedicatedStaffCount: parseAmountString(tenure.dedicatedStaff) || 0
      }))
    };
    const filteredBody = filterPayload(body)
    postService(BUDGET_SERVICE, `/budgets/v1`, filteredBody)
      .then((res) => {
        const newBudgetId = res.data?.id;
        history.push(`/budget/${newBudgetId}`,
          { fromMatter: fromMatter, currentMatterTab: currentMatterTab, isRowClick: true });
        setLoading(false);
        addSnack({
          message: BUDGET_UPDATE_MESSAGE,
        });
      })
      .catch((error) => {
        if (data?.hasOwnProperty("budgetPhases")) {
          data.budgetPhases = sparebudgetPhases;
        }
        catchError(error);
      });
  };




  // Validation Functions
  const isBudgetPhaseValid = (list, savedList) => {
    return true
  };


  // Metadata Configuration
  const metadataFields = [
    {
      label: MATTER_NAME,
      dataKey: 'matterName',
      isLink: true,
      linkUrl: `/matter/${matterData?.id || matterData?.identifier}`,
      fallback: 'N/A'
    },
    {
      label: MATTER_TYPE,
      dataKey: 'matterType',
      fallback: 'N/A'
    },
    {
      label: MATTER_SUBTYPE,
      dataKey: 'matterSubType',
      fallback: 'N/A'
    },
    {
      label: MATTER_DESCRIPTION,
      dataKey: 'description',
      fallback: 'N/A'
    },
    {
      label: JURISDICTION,
      dataKey: 'matterState',
      fallback: 'N/A'
    },
    {
      label: LEAD_ATTORNEY,
      dataKey: 'leadAttorney',
      isLink: true,
      linkUrl: `/people/${leadAttorney}/view`,
      fallback: 'N/A'
    },
    {
      label: LEAD_PARALEGAL,
      dataKey: 'leadParalegal',
      isLink: true,
      linkUrl: `/people/${leadParalegal}/view`,
      fallback: 'N/A'
    },
    ...(data?.matter?.practiceAreaCode === 'TORT_LITIGATION' ? [{
      label: RISKONNECT,
      dataKey: 'riskconnect',
      fallback: 'N/A'
    }] : []),
    {
      label: APPLICABLE_AFA,
      dataKey: 'applicableAFA',
      isLink: true,
      linkUrl: `/afa/${data?.applicableAFA}`,
      fallback: '-'
    },
    {
      label: RELATIONSHIP_PARTNER,
      dataKey: 'relationshipPartnerName',
      fallback: 'N/A'
    },
    {
      label: ESTIMATED_HOURS,
      dataKey: 'estimatedHours',
      fallback: 'N/A'
    },
    {
      label: WORK_PERIOD,
      dataKey: 'workPeriod',
      fallback: 'N/A'
    },
    {
      label: SCOPE_OF_WORK,
      dataKey: 'scopeOfWork',
      fallback: 'N/A'
    },
    {
      label: NOTES,
      dataKey: 'notes',
      fallback: 'N/A',
      truncate: 50,
      showTooltip: true
    }
  ];

  // Render Functions
  const renderMetadataItem = (field) => {
    const value = matterData?.[field.dataKey] || organizationData?.[field.dataKey];
    const displayValue = value || field.fallback;

    // Handle truncation and tooltip for notes field
    const getTruncatedValue = () => {
      if (field.truncate && displayValue && displayValue.length > field.truncate) {
        return displayValue.substring(0, field.truncate) + '...';
      }
      return displayValue;
    };

    const shouldShowTooltip = field.showTooltip && displayValue && displayValue.length > (field.truncate || 0);

    return (
      <div key={field.dataKey} className={classes.metadataItem}>
        <Label text={field.label} />
        {matterLoading ? (
          <Skeleton height="18px" variant="rectangle" />
        ) : (
          <div className={classes.metadataValue}>
            {field.isLink && value ? (
              <a
                href={field.linkUrl}
                className={classes.metadataLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                {getTruncatedValue()}
              </a>
            ) : shouldShowTooltip ? (
              <span
                style={{ cursor: 'help' }}
                title={displayValue}
              >
                {getTruncatedValue()}
              </span>
            ) : (
              getTruncatedValue()
            )}
          </div>
        )}
      </div>
    );
  };

  const handleEstimatedTotalCostBlur = (e) => {
    const parsedValue = parseAmountString(e.target.value);
    setEnteredCost(parsedValue);
    const breakdownTotalCost = tenureTableData?.reduce((sum, entry) => {
      const budgetAmount = parseFloat(entry.budgetAmount) || 0;
      return sum + budgetAmount;
    }, 0) || 0;
    
    // Set estimated total cost as max of breakdown total and user entered cost
    const maxCost = Math.max(breakdownTotalCost, parsedValue);
    setEstimatedTotalCost(amountFormat(maxCost));
  }
  const metaDataColumn = () => {
    return (
      <GridColumn sm={3}>
        <div className={classes.metadataColumn}>
          <Heading as="h3" size="large" UNSAFE_className={classes.subTitle}>
            {MATTER_INFORMATION}
          </Heading>
          <div className={classes.metadataContent}>
            {metadataFields.map(renderMetadataItem)}
          </div>
        </div>
      </GridColumn>
    );
  };

  // Main Render
  return (
    <div className={classes.container}>
      <Detail
        mode={mode}
        setMode={setMode}
        breadcrumbValues={[]}
        subject={title.edit}
        newTitle={title.create}
        onCreate={onSubmitHandle}
        onUpdate={onUpdateHandler}
        onDelete={() => { }}
        onCancel={onCancelHandler}
        loading={loading}
        isValid={Object.keys(errors).length > 0 ? false : isValid}
        isDirty={Object.keys(errors).length > 0 ? false : isDirty}
        customClassName={"intakeDetailContainer"}
        hideDeleteBtn={true}
        editBtnTxt={isMatterSelected ? SUBMIT : PROCEED}
        cancelText={isMatterSelected ? DISCARD : GO_BACK}
        fromBudget={matterData}
        preventSubmitOnEnter={true}
      // footerActions={<div>Version {mode === "edit" ? data.version+1 : '1.00'}</div>}
      >
        <>
          <Grid hasGutter>
            <GridColumn sm={9}>
              {/* Overview Section */}
              {!isMatterSelected ?
                <div className={[classes.columnSpacing, classes.divBorder].join(" ")}>
                  <Heading as="h3" size="large" UNSAFE_className={classes.sectionHeading}>
                    {OVERVIEW}
                  </Heading>
                  <Grid hasGutter>
                    <GridColumn sm={6}>
                      <Label text={BUDGET_ORGANIZATION} />
                      {loading || dataLoading ?
                        <Skeleton height="58px" variant="rectangle" /> :
                        <TextField
                          required={true}
                          value={companyName}
                          disabled={true}
                          register={register}
                          errors={errors}
                          name="organization"
                          validationRules={budgetValidationRules}
                          fromCreateBudget={true}
                          excludeNeverUse={true}
                          onKeyDown={handleKeyDown}
                        />
                      }
                    </GridColumn>
                    <GridColumn sm={6}>
                      <Label text={MATTER} />
                      {loading || dataLoading ?
                        <Skeleton height="58px" variant="rectangle" /> :
                        <MatterAutocomplete
                          required={true}
                          value={matterData}
                          setValue={(value) => {
                            handleInputChange("matter", value)
                          }}
                          register={register}
                          errors={errors}
                          name={"matter"}
                          validationRules={budgetValidationRules}
                          isPaSpecific={!budgetAccessibility?.addNew && budgetAccessibility?.addNewPA}
                        />
                      }
                    </GridColumn>
                  </Grid>
                </div>
                :
                <>
                  <div className={[classes.columnSpacing, classes.divBorder].join(" ")}>
                    <Heading as="h3" size="large" UNSAFE_className={classes.sectionHeading}>
                      {OVERVIEW}
                    </Heading>
                    <Grid hasGutter>
                      <GridColumn sm={12}>
                        <Label text={BUDGET_NAME} />
                        {loading || dataLoading ?
                          <Skeleton height="58px" variant="rectangle" />
                          :
                          <div className={classes.contetSpacing8}>
                            <MuiTextField
                              value={data?.budgetName}
                              data-testid="budget-name"
                              helperText={errors?.budgetName && <MuiValidationMessage message={errors?.budgetName?.message} />}
                              inputProps={{
                                id: "budgetName",
                                name: "budgetName"
                              }}
                              type="text"
                              fullWidth={true}
                              error={errors?.budgetName}
                              disabled
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        }
                      </GridColumn>
                    </Grid>
                    <div className={classes.marginVertical}>
                      <Grid hasGutter>
                        <GridColumn sm={4}>
                          <Label text={ESTIMATED_BLENDED_RATE} />
                          <div className={classes.contetSpacing8}>
                            <TextField
                              label={false}
                              type="text"
                              value={ blendedRate || parseAmountString(estimatedTotalHours) > 0 ? amountFormat(parseAmountString(estimatedTotalCost) / parseAmountString(estimatedTotalHours)) : "0.00"}
                              leadingIcon={<AttachMoney fontSize="small" />}
                              data-testid="estimated-blended-rate"
                              disabled={true}
                            />
                          </div>
                        </GridColumn>
                        <GridColumn sm={4}>
                          <Label text={ESTIMATED_TOTAL_HOURS} />
                          <div className={classes.contetSpacing8}>
                            <TextField
                              label={false}
                              type="text"
                              value={amountFormat(parseAmountString(estimatedTotalHours)) || "0.00"}
                              data-testid="estimated-total-hours"
                              disabled={true}
                            />
                          </div>
                        </GridColumn>
                        <GridColumn sm={4}>
                          <Label text={TOTAL_COST} />
                          <div className={classes.contetSpacing8}>
                            <TextField
                              label={false}
                              type="text"
                              value={estimatedTotalCost}
                              leadingIcon={<AttachMoney fontSize="small" />}
                              onChange={(e) => {
                                handleInputChange("estimatedTotalCost", e?.target?.value);
                              }}
                              onBlur={handleEstimatedTotalCostBlur}
                              onKeyDown={handleKeyDown}
                              data-testid="estimated-total-cost"
                              disabled={budgetTypeLocal !== "Total" || loading}
                            />
                          </div>
                        </GridColumn>
                      </Grid>
                    </div>
                  </div>

                  <div className={[classes.columnSpacing, classes.divBorder].join(" ")}>
                    <Heading as="h3" size="large" UNSAFE_className={classes.sectionHeading}>
                      Details
                    </Heading>
                    <div className={classes.marginVertical}>
                      <Grid hasGutter>
                        <GridColumn sm={6}>
                          <Heading as="h3" size="small" UNSAFE_className={classes.subTitle}>
                            Type*
                          </Heading>
                          {loading || dataLoading ?
                            <Skeleton height="58px" variant="rectangle" />
                            :
                            <Select
                              value={data?.budgetType}
                              onChange={(e) => {
                                handleInputChange("budgetType", e?.target?.value);
                                clearErrors("budgetAmount");
                              }}
                              disabled={mode === "edit"}
                              data-testid="budget-type-select"
                              placeholder="Select Budget Type"
                            >
                              <option value="Phase">Phase</option>
                              <option value="Task">Task / UTBMS Code</option>
                              <option value="Total">Total</option>

                            </Select>
                          }
                        </GridColumn>
                        <GridColumn sm={6}>
                          <Heading as="h3" size="small" UNSAFE_className={classes.subTitle}>
                            Time Period*
                          </Heading>
                          {loading || dataLoading ?
                            <Skeleton height="58px" variant="rectangle" />
                            :
                            <FormGroup id="time-period">
                              <Radio
                                label={LOM}
                                name="timePeriod"
                                id="life-of-matter"
                                value={BUDGET_LABELS.LIFE_OF_MATTER}
                                checked={data?.timePeriod === BUDGET_LABELS.LIFE_OF_MATTER}
                                data-testid="time-period-life_of_matter"
                                onChange={(e) =>
                                  handleInputChange(e.target.name, e.target.value)
                                }
                              />
                              <Radio
                                label={CFY}
                                name="timePeriod"
                                id="fiscal-year"
                                value={BUDGET_LABELS.CUSTOM_DATE_RANGE_LABEL}
                                checked={data?.timePeriod === BUDGET_LABELS.CUSTOM_DATE_RANGE_LABEL}
                                data-testid="time-period-fiscal-year"
                                onChange={(e) =>
                                  handleInputChange(e.target.name, e.target.value)
                                }
                              />
                            </FormGroup>
                          }
                          {data?.timePeriod === BUDGET_LABELS.CUSTOM_DATE_RANGE_LABEL && (
                            <div
                              className={classes.timePeriodSpacingTop}
                              data-testid="fiscal-year"
                            >
                              {loading || dataLoading ?
                                <Skeleton height="58px" variant="rectangle" />
                                :
                                <Grid>
                                  <GridColumn sm={10}>
                                    <Select
                                      value={data?.fiscalYear}
                                      onChange={(e) => {
                                        handleInputChange(
                                          "fiscalYear",
                                          e.target.value
                                        );
                                      }}>
                                      <option value="">Select Fiscal Year</option>
                                      {tenYears?.map((item) => <option key={item.slice(0, 6)} value={item.slice(0, 6)}>{item}</option>)}
                                    </Select>
                                  </GridColumn>
                                </Grid>
                              }
                            </div>
                          )}
                        </GridColumn>
                      </Grid>
                    </div>
                    {data?.budgetType?.toLowerCase() === "task" && (
                      <>
                        <hr className={classes.customHr} />
                        <div className={classes.marginVertical}>
                          <AddBudgetByUTBMS
                            loading={loading}
                            dataLoading={dataLoading}
                            masterDetails={masterDetails}
                            mode={mode}
                            data={data}
                            onUtbmsDataChange={handleUtbmsDataChange}
                            initialData={data?.utbmsEntries || []}
                          />
                        </div>
                      </>
                    )}

                    {data?.budgetType?.toLowerCase() === "phase" && (
                      <>
                        <hr className={classes.customHr} />
                        <div className={classes.marginVertical}>
                          <AddBudgetByPhase
                            loading={loading}
                            dataLoading={dataLoading}
                            data={data}
                            practiceAreaCode={data?.matter?.practiceAreaCode}
                            setLoading={setLoading}
                            onPhasesChange={handlePhasesChange}
                          />
                        </div>
                      </>
                    )}

                    <hr className={classes.customHr} />
                    {/* Estimation of Hours by Tenure Table */}
                    <BreakdownByTenure
                    loading={loading}
                      
                      tenureTableData={tenureTableData}
                      handleRemoveTenureRow={handleRemoveTenureRow}
                      handleTenureDataChange={handleTenureDataChange}
                      showActions={true}
                    />
                  </div>
                </>
              }
            </GridColumn>
            {metaDataColumn()}
          </Grid>
        </>
      </Detail>
      <SubmitBudgetModal
        open={submitModalOpen}
        setOpen={setSubmitModalOpen}
        onCloseModal={onCloseModal}
        title={BUDGET_LABELS.EXISTING_BUDGET}
        submitButtonText={PROCEED}
        onSubmit={onCreateHandler}
        submitLoading={loading}
        budgetData={existingBudgetData}
      />
    </div>
  );
};

export default CreateBudgetIntake;
n